import * as _ from 'underscore';
const compareObjects = require('fast-deep-equal');

import { IGlob } from '../../../../pdc-platform/src/ts/Glob';
import * as DateTimeFunctions from '../../ts/DateTimeFunctions';
import GlobalState from '../../ts/globalStates/GlobalState';
import { IValuesCollection } from '../../ts/appTheme';

import { RELATIONS, RELATION_VALUES, URL_KEYS, VARIABLE_USER_ID } from './Constants';

import {
    AttributeSelectionType,
    IAttribute,
    IQueryFilter,
    QueryFilterValueType,
    IAttributeOptions,
    IAttributeDataQuery,
    IAttributeName,
    IGlobalAttributeFilter,
    IAQLFilter,
    IScopeOptions,
    IAttributeTreePropertiesBase,
    IGlobalAttributeFilterCollection,
    IGlobalAttributeFilters,
    IPeriodFilter,
    IGlobalFilterControl_AttributeFilter
} from './Interfaces';

import { IDisplayValue } from '../../../../_common/platform/ts/Interfaces';
import { themeVars as platformThemeVars } from '../../../../pdc-platform/src/ts/themes/themeConstants';

import {
    ATTRIBUTE_TYPES,
    ATTRIBUTE_CONTROL_TYPES,
    TIME_PART_TYPES,
    TIME_PART_ELEMENTS,
    VALID_LOCAL_FILTER_ATTRIBUTE_CONTROL_TYPES,
    VALID_ATTRIBUTE_CONTROL_TYPES,
    getElementNameByType,
    ITypeNameElement
} from '../../ts/ConfigurationConstants';

import {
    EntityModelsStore,
    ATTRIBUTE_SEPARATOR,
    IExpression
} from '../../../../pdc-platform/src/ts/stores/EntityModelsStore';

import { IIntervals, AQL } from '../../ts/AqlUtility';
import { IURLQueryValue } from '../../../../pdc-platform/src/ts/Interfaces';

export const ApplyModes = {
    AUTO: 'auto',
    SEMI_AUTO: 'semiauto',
    MANUAL: 'manual'
};

export const ApplySwitcherStates = {
    HIDDEN: 'hidden',
    OFF_BY_DEFAULT: 'offByDefault',
    ON_BY_DEFAULT: 'onByDefault'
};

export function isSpecialRelation(relation: string) {
    return relation === RELATIONS.IGNORE || relation === RELATIONS.REQUIRE_VALUE;
}

export function isOneOfRelation(relation: string) {
    return relation === RELATIONS.IS_ONE_OF || relation === RELATIONS.IS_NOT_ONE_OF;
}

export function isNullRelation(relation: string) {
    return relation === RELATIONS.IS_NULL || relation === RELATIONS.IS_NOT_NULL;
}

export function isExistsRelation(relation: string) {
    return relation === RELATIONS.EXISTS || relation === RELATIONS.NOT_EXISTS;
}

export function isIsSubnodeOfRelation(relation: string) {
    return relation === RELATIONS.IS_SUBNODE_OF || relation === RELATIONS.IS_NOT_SUBNODE_OF;
}

export function getValidRelations(attributeType: string, selectionType?: AttributeSelectionType) {
    if (attributeType == null) {
        // For Entities without Default Attribute.
        return [RELATIONS.EXISTS];
    } else if (selectionType == null || selectionType === 'single') {
        return attributeType === ATTRIBUTE_TYPES.BIGINT
            ? RELATION_VALUES[ATTRIBUTE_TYPES.INTEGER]
            : RELATION_VALUES[attributeType];
    } else {
        return [RELATIONS.IS_ONE_OF, RELATIONS.IS_NOT_ONE_OF];
    }
}

export function getVariableType(
    controlType: string,
    attributeName: string,
    options: IAttributeOptions,
    dataSourceID: number,
    glob: IGlob,
    runTimeWidgetID?: number
) {
    let result = convertControlTypeToAttributeType(controlType);

    if (result === ATTRIBUTE_TYPES.ENUMERATION) {
        if (options == null) {
            return null;
        }

        let dataQueryAttribute: string;

        if (options.dataQuery != null) {
            dataQueryAttribute = options.dataQuery.attribute;
        } else if (options.variableName != null) {
            dataQueryAttribute = attributeName;
        }

        if (dataQueryAttribute != null) {
            if (controlType === ATTRIBUTE_CONTROL_TYPES.TREE) {
                const kp = getAttributesForTree(dataQueryAttribute, options, dataSourceID, glob, runTimeWidgetID);

                if (kp == null) {
                    return null;
                }

                dataQueryAttribute = kp.attributeWithKey;
            }

            result = glob.EntityModelsStore.getDefaultAttributeType(dataQueryAttribute, dataSourceID);
        } else if (controlType === ATTRIBUTE_CONTROL_TYPES.TREE) {
            result = ATTRIBUTE_TYPES.INTEGER;
        }
    }

    return result;
}

export function normalizeVariableName(variable: string) {
    if (variable.indexOf('@') !== 0) {
        return '@' + variable;
    }

    return variable;
}

export function variableNamesAreEqual(variable1: string, variable2: string) {
    if (variable1 != null && variable2 != null && variable1.length > 0 && variable2.length > 0) {
        if (variable1 === variable2) {
            return true;
        }

        variable1 = normalizeVariableName(variable1);
        variable2 = normalizeVariableName(variable2);

        return variable1 === variable2;
    }

    return false;
}

export function checkVariableType(
    controlType: string,
    attributeType: string,
    dataSourceID: number,
    attributeName: string,
    globalFilterOptions: IAttributeOptions,
    glob: IGlob,
    runTimeWidgetID: number
) {
    // When filter is initialized from URL, the controlType is undefined
    // Assume that variableType is equal attributeType
    if (controlType == null) {
        return true;
    }

    if (isOfTimePartControlType(controlType)) {
        if (DateTimeFunctions.isTypeOfAnyDate(attributeType)) {
            // Time Part Varibales are set up for date Attributes automaticaly.
            return true;
        }
    }

    const variableType = getVariableType(
        controlType,
        attributeName,
        globalFilterOptions,
        dataSourceID,
        glob,
        runTimeWidgetID
    );

    if (variableType == null) {
        return false;
    }

    // Assignment of an integer Varibale to a decimal Attribute is allowed.
    if (attributeType === ATTRIBUTE_TYPES.REAL && variableType === ATTRIBUTE_TYPES.INTEGER) {
        return true;
    }
    // else if (controlType === ATTRIBUTE_CONTROL_TYPES.TREE) {
    //     const kp = getAttributesForTree(dataQueryAttribute, options, dataSourceID, glob, runTimeWidgetID);

    //     if (kp == null) {
    //         return null;
    //     }

    //     dataQueryAttribute = kp.attributeWithKey;
    // }

    if (variableType !== attributeType) {
        return false;
    }

    return true;
}

export function isValueEmpty(value: any) {
    return value == null || (Array.isArray(value) && value.length === 0);
}

export function getFilterVariableName(filter: IAttribute) {
    if (filter == null) {
        return undefined;
    }

    if (filter.valueType === 'variable') {
        return filter.attribute;
    } else {
        return filter.options?.variableName;
    }
}

export function findVariables(
    filterVariables: IGlobalAttributeFilter[],
    variableName: string,
    getNotEmpty: boolean
): IGlobalAttributeFilter[] {
    if (filterVariables == null) {
        return null;
    }

    const variables = filterVariables.filter((filter) => {
        const filterVariableName = getFilterVariableName(filter);

        let result = filterVariableName != null && variableNamesAreEqual(filterVariableName, variableName);

        if (result && getNotEmpty) {
            result = !isValueEmpty(filter.values);
        }

        return result;
    });

    return variables.length > 0 ? variables : null;
}

export function findFirstVariable(
    filterVariables: IGlobalAttributeFilter[],
    variableName: string,
    getNotEmpty: boolean
): IGlobalAttributeFilter {
    const variables = findVariables(filterVariables, variableName, getNotEmpty);
    return variables != null ? variables[0] : null;
}

// As soon as & is used to split arguments, it must be replaced with another symbol.
// Encoding of & as %26 does not help. ';26;' looks strange enougth to be a replacement.
// % is not tolerated either. ';25;' is a replacement.
export function encodeParameterForURL(value: string) {
    if (typeof value !== 'string') {
        value = (value as any).toString();
    }

    value = value.replace(/&/g, ';26;');
    value = value.replace(/%/g, ';25;');
    return encodeURIComponent(value);
}

export function encodeParametersForURL(value: string[]) {
    if (Array.isArray(value)) {
        return value.reduce((previousValue, currentValue) => {
            if (currentValue == null) {
                return previousValue;
            }

            if (previousValue.length > 0) {
                previousValue += ',';
            }

            return previousValue + encodeParameterForURL(currentValue);
        }, '');
    } else {
        return encodeParameterForURL(value);
    }
}

export function decodeParameterForURL(value: string) {
    value = decodeURIComponent(value);
    value = value.replace(/;25;/g, '%');
    value = value.replace(/;26;/g, '&');
    return value;
}

// See Issue 98586. A value having comma inside breaks parsing of array of values as it is comma separated.
// So, comma is encoded as ';2C;'
export function encodeParameterValueForURL(value: string) {
    if (typeof value !== 'string') {
        value = (value as any).toString();
    }

    return value.replace(/,/g, ';2C;');
}

export function decodeParameterValueForURL(value: string) {
    if (typeof value !== 'string') {
        value = (value as any).toString();
    }

    return value.replace(/;2C;/g, ',');
}

export function parseURLAttribute(
    key: string,
    queryValue: IURLQueryValue
): {
    attributeName?: IAttributeName;
    attributeType?: string;
    attributeVariable?: string;
    isAttribute?: boolean;
    isVariable?: boolean;
    isExpression?: boolean;
    isTimePart?: boolean;
} {
    if (key == null || key.length === 0) {
        return {};
    }

    if (key.indexOf(URL_KEYS.Expression) === 0) {
        const expression: IExpression = JSON.parse(atob(queryValue.attr.slice(URL_KEYS.Expression.length)));

        return {
            attributeName: {
                expression: expression
            },
            attributeType: expression.rettype,
            isExpression: true
        };
    }

    if (key.indexOf(URL_KEYS.TimePart) === 0) {
        return {
            attributeName: {
                attribute: key.replace(/timePart\./gi, '')
            },
            isTimePart: true
        };
    }

    const indexOfAttributeWithVariable = key.indexOf(URL_KEYS.AttributeWithVariable);

    if (indexOfAttributeWithVariable > 0) {
        return {
            attributeName: {
                attribute: key.substring(0, indexOfAttributeWithVariable)
            },
            attributeVariable: key.substring(indexOfAttributeWithVariable + URL_KEYS.AttributeWithVariable.length),
            isAttribute: true
        };
    }

    let attribute = decodeParameterForURL(queryValue.attr);

    if (key.indexOf('@') === 0) {
        let variableName = attribute;
        let attributeType: string;

        const indexOfType = variableName.indexOf(URL_KEYS.Type);

        if (indexOfType > 0) {
            variableName = attribute.slice(0, indexOfType);
            attributeType = attribute.slice(indexOfType + URL_KEYS.Type.length);
        } else {
            // For backward compatibility.
            attributeType = ATTRIBUTE_TYPES.STRING;
        }

        return {
            attributeName: {
                attribute: variableName
            },
            attributeType: attributeType,
            isVariable: true
        };
    }

    return {
        attributeName: {
            attribute: attribute
        },
        isAttribute: true
    };
}

export function getApplyMode(theme: IValuesCollection): string {
    let result = theme['workspace-apply-mode'] as string;

    if (result == null) {
        //20.30.2020: It is for compartibility. Drop it later.
        result = theme['use-apply-button'] === true ? ApplyModes.MANUAL : ApplyModes.AUTO;
    }

    return result;
}

export function useApplyButton(theme: IValuesCollection): boolean {
    return getApplyMode(theme) !== ApplyModes.AUTO;
}

//EVG: 20.12.2018: It is for migration only. Drop it later.
export function getValueType(obj: IQueryFilter): QueryFilterValueType {
    let result: QueryFilterValueType;

    if (obj != null) {
        result = obj.valueType;

        if (result == null && obj.options != null) {
            result = obj.options.valueType;
        }
    }

    return result;
}

export function isConstantValueType(valueType: QueryFilterValueType): boolean {
    return valueType == null || valueType === 'constant';
}

export function hasConstantValueType(obj: { valueType?: QueryFilterValueType }): boolean {
    return isConstantValueType(getValueType(obj));
}

export function convertControlTypeToAttributeType(controlType: string) {
    switch (controlType) {
        case ATTRIBUTE_CONTROL_TYPES.BOOLEAN:
            return ATTRIBUTE_TYPES.BOOLEAN;

        case ATTRIBUTE_CONTROL_TYPES.DATE:
            return ATTRIBUTE_TYPES.DATE;

        case ATTRIBUTE_CONTROL_TYPES.DATETIME:
            return ATTRIBUTE_TYPES.DATETIME;

        case ATTRIBUTE_CONTROL_TYPES.TIME:
            return ATTRIBUTE_TYPES.TIME;

        case ATTRIBUTE_CONTROL_TYPES.DECIMAL:
            return ATTRIBUTE_TYPES.REAL;

        case ATTRIBUTE_CONTROL_TYPES.NUMBER: //12.09.2019: Obsolete.
        case ATTRIBUTE_CONTROL_TYPES.INTEGER:
            return ATTRIBUTE_TYPES.INTEGER;

        case ATTRIBUTE_CONTROL_TYPES.TEXT:
            return ATTRIBUTE_TYPES.STRING;

        default:
            if (isOfTimePartControlType(controlType)) {
                return ATTRIBUTE_TYPES.INTEGER;
            } else {
                return ATTRIBUTE_TYPES.ENUMERATION;
            }
    }
}

export function convertAttributeTypeToControlType(attributeType: string) {
    switch (attributeType) {
        case ATTRIBUTE_TYPES.BOOLEAN:
        case ATTRIBUTE_TYPES.LOGICAL:
            return ATTRIBUTE_CONTROL_TYPES.BOOLEAN;

        case ATTRIBUTE_TYPES.DATE:
            return ATTRIBUTE_CONTROL_TYPES.DATE;

        case ATTRIBUTE_TYPES.DATETIME:
            return ATTRIBUTE_CONTROL_TYPES.DATETIME;

        case ATTRIBUTE_TYPES.TIME:
            return ATTRIBUTE_CONTROL_TYPES.TIME;

        case ATTRIBUTE_TYPES.REAL:
            return ATTRIBUTE_CONTROL_TYPES.DECIMAL;

        case ATTRIBUTE_TYPES.INTEGER:
            return ATTRIBUTE_CONTROL_TYPES.INTEGER;

        case ATTRIBUTE_TYPES.STRING:
            return ATTRIBUTE_CONTROL_TYPES.TEXT;

        default:
            return ATTRIBUTE_CONTROL_TYPES.DROPDOWN;
    }
}

export function getAttributesForTree(
    attribute: string,
    options: IAttributeOptions,
    dataSourceID: number,
    glob: IGlob,
    runTimeWidgetID: number
): {
    entityName: string;
    keyAttribute: string;
    parentAttribute: string;
    attributeWithKey: string;
} {
    let parentAttribute: string;

    if (options != null) {
        parentAttribute = options.parentAttribute;
    }

    const entityModelsStore = glob.EntityModelsStore;

    let entity = entityModelsStore.getReferredEntity(attribute, dataSourceID);

    if (entity == null) {
        glob.AppActions.showNotification_Fail(
            `Entity is not found for Tree Attrbute: '${attribute}'.`,
            runTimeWidgetID
        );
        return;
    }

    let keyAttribute: string;
    let attributeWithKey: string;

    const isFunction = entityModelsStore.isFunction(entity);

    if (isFunction) {
        const dataQuery = options != null && options.dataQuery != null && options.dataQuery ? options.dataQuery : null;

        if (dataQuery != null) {
            entity = entityModelsStore.getReferredEntity(dataQuery.attribute, dataSourceID);
            parentAttribute = dataQuery.parentAttribute;
            // See Issue 94348.
            attributeWithKey = attribute; // It was this way. keyAttribute has not been specified.
            keyAttribute = EntityModelsStore.getEntityKeyAttribute(entity);

            if (keyAttribute != null) {
                attributeWithKey += ATTRIBUTE_SEPARATOR + keyAttribute;
            }
        }
    } else {
        keyAttribute = EntityModelsStore.getEntityKeyAttribute(entity);

        if (keyAttribute != null) {
            attributeWithKey = attribute + ATTRIBUTE_SEPARATOR + keyAttribute;
        } else {
            glob.AppActions.showNotification_Fail(
                `Key Attribute is not found for Tree Attrbute: '${attribute}'.`,
                runTimeWidgetID
            );
            return;
        }
    }

    if (parentAttribute == null) {
        parentAttribute = entityModelsStore.getEntityDefaultParentAttribute(entity);
    }

    return {
        entityName: entity.name,
        keyAttribute: keyAttribute,
        parentAttribute: parentAttribute,
        attributeWithKey: attributeWithKey
    };
}

export function isOfTimePartControlType(value: string) {
    return (
        value != null &&
        value !== TIME_PART_TYPES.NONE &&
        _.find(TIME_PART_ELEMENTS.dateTimeInterval, (item) => {
            return item.type === value;
        }) != null
    );
}

export function isFiscalTimePart(timePart: string) {
    return (
        timePart === TIME_PART_TYPES.FISCAL_YEAR ||
        timePart === TIME_PART_TYPES.FISCAL_QUARTER ||
        timePart === TIME_PART_TYPES.HALF_FISCAL_YEAR
    );
}

export function isFullYearTimePart(timePart: string) {
    return timePart === TIME_PART_TYPES.YEAR || timePart === TIME_PART_TYPES.FISCAL_YEAR;
}

export function getTimePartNameByType(timePart: string) {
    return getElementNameByType(timePart, TIME_PART_ELEMENTS.dateTimeInterval);
}

// export function getControlType(value: IAttribute, attributeType: string): string {
//     if (value == null || attributeType === ATTRIBUTE_TYPES.LOGICAL) {
//         return undefined;
//     }

//     if (value.valueType === 'timePart') {
//         return value.attribute;
//     } else {
//         //12.09.2019: Number is obsolete. It needs to be fixed for old widgets.
//         return value.controlType === ATTRIBUTE_CONTROL_TYPES.NUMBER
//             ? ATTRIBUTE_CONTROL_TYPES.INTEGER
//             : value.controlType;
//     }
// }

export interface IControlAndAttributeTypes {
    attributeType?: string;
    defaultAttributeType?: string;
    controlType?: string;
}

export function getControlAndAttributeTypes(
    value: {
        controlType?: string;
        options?: IAttributeOptions;
        valueType?: QueryFilterValueType;
        attribute?: string;
        expression?: IExpression;
    },
    dataSourceID: number,
    glob: IGlob
): IControlAndAttributeTypes {
    if (value == null) {
        return {};
    }

    const relation = value.options != null ? value.options.relation : null;

    let attributeType: string;
    let defaultAttributeType: string;
    let validControlTypes: ITypeNameElement[];
    //12.09.2019: Number is obsolete. It needs to be fixed for old widgets.
    let controlType =
        value.controlType === ATTRIBUTE_CONTROL_TYPES.NUMBER ? ATTRIBUTE_CONTROL_TYPES.INTEGER : value.controlType;

    switch (value.valueType) {
        case 'variable':
            attributeType = defaultAttributeType = convertControlTypeToAttributeType(controlType);
            validControlTypes = getValidControlTypes(ATTRIBUTE_TYPES.VARIABLE, relation, false);
            break;

        case 'timePart':
            attributeType = defaultAttributeType = ATTRIBUTE_TYPES.INTEGER;
            controlType = value.attribute;
            validControlTypes = [
                {
                    type: controlType,
                    name: controlType
                }
            ];
            break;

        default:
            attributeType = glob.EntityModelsStore.getAttributeType(value, dataSourceID);
            defaultAttributeType = glob.EntityModelsStore.getDefaultAttributeType(value, dataSourceID);
            validControlTypes = getValidControlTypes(attributeType, relation, false);
            break;
    }

    if (attributeType === ATTRIBUTE_TYPES.LOGICAL) {
        controlType = undefined;
    } else {
        if (validControlTypes != null) {
            if (
                controlType == null ||
                _.find(validControlTypes, (validType) => {
                    return validType.type === controlType;
                }) == null
            ) {
                controlType = validControlTypes[0].type;
            }
        } else {
            controlType = undefined;
        }
    }

    return {
        attributeType: attributeType,
        defaultAttributeType: defaultAttributeType,
        controlType: controlType
    };
}

// Should be used for Attributes, but Variables. Variables can have values always.
export function relationDoesNotAllowToHaveValue(relation: string) {
    return relation == null || isNullRelation(relation) || isExistsRelation(relation) || isSpecialRelation(relation);
}

export function filterValueTypeCannotBeChanged(attributeType: string, relation: string) {
    return (
        attributeType == null || attributeType === ATTRIBUTE_TYPES.LOGICAL || relationDoesNotAllowToHaveValue(relation)
    );
}

export function attributeCannotHaveValue(value: IAttribute, attributeType: string) {
    if (value != null) {
        let relation: string;

        if (value.options != null) {
            relation = value.options.relation;
        }

        if (relation == null) {
            const relationValues = getValidRelations(attributeType, value.selectionType);

            if (relationValues != null) {
                relation = relationValues[0];
            }
        }

        return relationDoesNotAllowToHaveValue(relation);
    }

    return false;
}

export function getValidTimeParts(attributeType: string, removeNone = true): ITypeNameElement[] {
    let result: ITypeNameElement[];

    if (attributeType === ATTRIBUTE_TYPES.VARIABLE || attributeType === ATTRIBUTE_TYPES.DATETIME) {
        result = TIME_PART_ELEMENTS.dateTimeInterval;
    } else if (attributeType === ATTRIBUTE_TYPES.DATE) {
        result = TIME_PART_ELEMENTS.dateInterval;
    } else if (attributeType === ATTRIBUTE_TYPES.TIME) {
        result = TIME_PART_ELEMENTS.timeInterval;
    }

    if (result != null) {
        result = [...result];

        if (removeNone) {
            result.splice(0, 1);
        }
    }

    return result;
}

export function getTimePartValueRange(
    timePartType: string,
    minYear?: number,
    maxYear?: number
): {
    start: number;
    end: number;
} {
    switch (timePartType) {
        case TIME_PART_TYPES.YEAR:
        case TIME_PART_TYPES.FISCAL_YEAR:
            return {
                start: minYear || 0,
                end: maxYear || 2100
            };

        case TIME_PART_TYPES.HALF_YEAR:
        case TIME_PART_TYPES.HALF_FISCAL_YEAR:
            return {
                start: 1,
                end: 2
            };

        case TIME_PART_TYPES.QUARTER:
        case TIME_PART_TYPES.FISCAL_QUARTER:
            return {
                start: 1,
                end: 4
            };

        case TIME_PART_TYPES.MONTH:
            return {
                start: 1,
                end: 12
            };

        case TIME_PART_TYPES.WEEK:
        case TIME_PART_TYPES.ISOWEEK:
            return {
                start: 1,
                end: 53
            };

        case TIME_PART_TYPES.DAY:
            return {
                start: 1,
                end: 31
            };

        case TIME_PART_TYPES.DAY_OF_YEAR:
            return {
                start: 1,
                end: 366
            };

        case TIME_PART_TYPES.DAY_OF_WEEK:
            return {
                start: 1,
                end: 7
            };

        case TIME_PART_TYPES.HOUR:
            return {
                start: 0,
                end: 23
            };

        case TIME_PART_TYPES.MINUTE:
        case TIME_PART_TYPES.SECOND:
            return {
                start: 0,
                end: 59
            };

        case TIME_PART_TYPES.MILLISECOND:
            return {
                start: 0,
                end: 59999
            };

        default:
            return null;
    }
}

export function isOfValidTimePartControlType(controlType: string, attributeType: string) {
    const validTimeParts = getValidTimeParts(attributeType);

    if (validTimeParts != null) {
        return (
            _.find(validTimeParts, (item) => {
                return item.type === controlType;
            }) != null
        );
    }

    return false;
}

export function getValidControlTypes(
    attributeType: string,
    relation: string,
    forLocalFilter: boolean
): ITypeNameElement[] {
    if (isIsSubnodeOfRelation(relation)) {
        return [
            {
                type: ATTRIBUTE_CONTROL_TYPES.TREE,
                name: 'Tree'
            }
        ];
    }

    let result = forLocalFilter
        ? VALID_LOCAL_FILTER_ATTRIBUTE_CONTROL_TYPES[attributeType]
        : VALID_ATTRIBUTE_CONTROL_TYPES[attributeType];

    if (result != null) {
        result = [...result];

        const validTimeParts = getValidTimeParts(attributeType);

        if (validTimeParts != null) {
            result = result.concat(validTimeParts);
        }
    }

    return result;
}

export function setIntervalsByControlType(
    obj: { intervals?: IIntervals },
    attribute: string,
    valueType: QueryFilterValueType,
    controlType: string,
    dataSourceID: number,
    glob: IGlob
) {
    let attributeType: string;

    switch (valueType) {
        case 'variable':
            attributeType = ATTRIBUTE_TYPES.VARIABLE;
            break;

        case 'timePart':
            attributeType = ATTRIBUTE_TYPES.DATETIME; // It should be valid for Time Part Control Type here.
            break;

        default:
            attributeType = glob.EntityModelsStore.getAttributeType(attribute, dataSourceID);
    }

    if (isOfValidTimePartControlType(controlType, attributeType)) {
        if (obj.intervals == null) {
            obj.intervals = {};
        }

        obj.intervals.timePart = controlType;

        if (!isFiscalTimePart(controlType)) {
            delete obj.intervals.timePeriodStartMonth;
        }
    } else {
        delete obj.intervals;
    }
}

export function isOfDisplayValueType(value: any) {
    if (value == null) {
        return false;
    }

    if (Array.isArray(value)) {
        if (value.length > 0) {
            return isOfDisplayValueType(value[0]);
        } else {
            return false;
        }
    } else {
        return typeof (value as IDisplayValue).value !== 'undefined';
    }
}

export function isInRange(value: string, min: number, max: number) {
    if (value == null) {
        return false;
    }

    const valueInt = parseInt(value, 10);

    if (isNaN(valueInt)) {
        return false;
    }

    return valueInt >= min && valueInt <= max;
}

export function normalizeTreeOptionsValue(value: IAttributeTreePropertiesBase): void {
    if (value == null) {
        return;
    }

    if (value.parentAttribute === '') {
        delete value.parentAttribute;
    }

    if (value.labelAttribute === '') {
        delete value.labelAttribute;
    }
}

export function normalizeDataQueryValue(
    value: IAttributeDataQuery,
    controlType: string,
    valueType: QueryFilterValueType
): void {
    if (value == null) {
        return;
    }

    if (value.attribute === '') {
        delete value.attribute;
    }

    if (_.isEmpty(value.expression)) {
        delete value.expression;
    }

    if (value.attributeToShow?.attribute === '' && _.isEmpty(value.attributeToShow?.expression)) {
        delete value.attributeToShow;
    }

    if (value.limit === null || (value.limit != null && value.limit <= 0)) {
        delete value.limit;
    }

    if (value.filter === null || (value.filter != null && value.filter.length === 0)) {
        delete value.filter;
    }

    if (_.isEmpty(value.globalFilterControl)) {
        delete value.globalFilterControl;
    }

    normalizeTreeOptionsValue(value);

    if (controlType === ATTRIBUTE_CONTROL_TYPES.TREE) {
        if (valueType === 'variable' && value.attribute == null) {
            // Data Query Attribute is optional for Attributes, but is mandatory for Variables.
            delete value.parentAttribute;
            delete value.labelAttribute;
            delete value.rootNode;
        }
    } else {
        delete value.parentAttribute;
        delete value.labelAttribute;
        delete value.rootNode;
        delete value.notSelectableParentNodes;
        delete value.autoSelectChildNodes;
        delete value.selectFirstNodeByDefault;
    }
}

export function isKeySelect(controlType: string, attributeOptions: IAttributeOptions, dataQuery?: IAttributeDataQuery) {
    if (dataQuery === undefined) {
        dataQuery = attributeOptions?.dataQuery;
    }

    return (
        controlType === ATTRIBUTE_CONTROL_TYPES.TREE ||
        ((controlType === ATTRIBUTE_CONTROL_TYPES.DROPDOWN || controlType === ATTRIBUTE_CONTROL_TYPES.SLIDER) &&
            dataQuery != null &&
            dataQuery.attributeToShow != null)
    );
}

export function convertValueToDisplayValue(
    value: IDisplayValue | string | number | boolean | { id: string; title: string },
    keySelect: boolean
): IDisplayValue {
    if (value != null) {
        if (typeof (value as IDisplayValue).value !== 'undefined') {
            if (keySelect) {
                return {
                    value: (value as IDisplayValue).value,
                    displayValue: (value as IDisplayValue).displayValue
                };
            } else {
                // Cleaning up.
                return {
                    value: (value as IDisplayValue).value
                };
            }
        } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            // For backward compatibility.
            if (!keySelect) {
                return {
                    value: value.toString()
                };
            }
        } else {
            // For backward compatibility.
            const title = (value as any).title;

            if (title != null) {
                const id = (value as any).id;

                if (id != null) {
                    return {
                        value: id,
                        displayValue: title
                    };
                } else if (!keySelect) {
                    return {
                        value: title
                    };
                }
            }
        }
    }

    return null;
}

export function convertValuesToDisplayValues(values: any, keySelect: boolean): IDisplayValue[] {
    let result: IDisplayValue[] = null;

    if (values != null) {
        if (Array.isArray(values)) {
            result = [];

            _.each(values, (value) => {
                const displayValue = convertValueToDisplayValue(value, keySelect);

                if (displayValue != null) {
                    result.push(displayValue);
                }
            });
        } else {
            const value = convertValueToDisplayValue(values, keySelect);

            result = value != null ? [value] : [];
        }
    }

    return result;
}

export function getNormalizedDisplayValues(
    values: string | string[] | IDisplayValue[],
    keySelect: boolean
): {
    values?: IDisplayValue[];
    stringValues?: string[];
    firstValue?: string;
} {
    if (values == null) {
        return {};
    }

    const resultValues = convertValuesToDisplayValues(values, keySelect);
    const stringValues: string[] = [];

    _.each(resultValues, (value) => {
        stringValues.push(value.value);
    });

    return {
        values: resultValues,
        stringValues: stringValues,
        firstValue: stringValues.length > 0 ? stringValues[0] : null
    };
}

// Returns new array of values formated by specified type.
export function formatArrayValuesByType(
    array: any[],
    attributeType: string,
    intervals?: IIntervals,
    timeZone?: DateTimeFunctions.ITimeZone
) {
    if (array != null && array.length > 0) {
        const result = [];

        array.forEach((value) => {
            const formattedValue = formatValueByType(value, attributeType, intervals, timeZone);

            // If undefined, then format is failed.
            if (formattedValue !== undefined) {
                result.push(formattedValue);
            }
        });

        return result.length > 0 ? result : undefined;
    } else {
        return array;
    }
}

function checkForNaN(value: any): any {
    if (isNaN(value)) {
        return undefined;
    }

    return value;
}

export function formatValueByType(
    value: any,
    attributeType: string,
    intervals?: IIntervals,
    timeZone?: DateTimeFunctions.ITimeZone
): any {
    if (value != null) {
        if (Array.isArray(value)) {
            return formatArrayValuesByType(value, attributeType, intervals, timeZone);
        } else {
            if (isOfDisplayValueType(value)) {
                value = (value as IDisplayValue).value;
            }

            if (variableNamesAreEqual(value, VARIABLE_USER_ID)) {
                //VR: using @UserID in DataPermission
                return value;
            }

            switch (attributeType) {
                case ATTRIBUTE_TYPES.INTEGER:
                    return checkForNaN(parseInt(value, 10));

                case ATTRIBUTE_TYPES.REAL:
                    return checkForNaN(parseFloat(value));

                case ATTRIBUTE_TYPES.STRING:
                    return value.toString();

                case ATTRIBUTE_TYPES.BOOLEAN:
                case ATTRIBUTE_TYPES.LOGICAL:
                    if (typeof value !== 'boolean') {
                        if (value === 'true') {
                            return true;
                        } else if (value === 'false') {
                            return false;
                        } else {
                            return undefined;
                        }
                    }

                default:
                    if (intervals != null && intervals.timePart != null) {
                        // Variable with Time Part.
                        return checkForNaN(parseInt(value, 10));
                    } else if (DateTimeFunctions.isTypeOfDateOrDatetime(attributeType)) {
                        return DateTimeFunctions.formatDateForBackendByType(value, attributeType, timeZone);
                    }
            }

            return value;
        }
    }

    return null;
}

export function isLogicalExpression(value: IExpression) {
    return value != null && value.rettype === ATTRIBUTE_TYPES.LOGICAL;
}

// Logical Expression is not applied when it is set false explicitly.
export function logicalExpressionIsOff(value: any) {
    return (
        value === false ||
        (Array.isArray(value) &&
            value.length > 0 &&
            (value[0].toString() === 'false' || (value[0] as IDisplayValue).value === 'false'))
    );
}

export function getDataQueryAttributes(
    dataQuery: IAttributeDataQuery,
    filterAttribute: IAttributeName,
    keySelect: boolean
) {
    if (dataQuery == null) {
        return null;
    }

    let attribute: string;
    let expression: IExpression;
    let keyAttribute: IAttributeName;

    const { attributeToShow } = dataQuery;

    if (attributeToShow != null) {
        if (attributeToShow.expression != null) {
            expression = attributeToShow.expression;
            keySelect = true;
        } else if (attributeToShow.attribute != null) {
            attribute = attributeToShow.attribute;
            keySelect = true;
        }

        if (keySelect) {
            if (dataQuery.expression != null) {
                keyAttribute = {
                    expression: dataQuery.expression
                };
            } else if (dataQuery.attribute != null) {
                keyAttribute = {
                    attribute: dataQuery.attribute
                };
            }

            if (keyAttribute == null) {
                if (filterAttribute.expression != null) {
                    keyAttribute = {
                        expression: filterAttribute.expression
                    };
                } else if (filterAttribute.attribute != null) {
                    keyAttribute = {
                        attribute: filterAttribute.attribute
                    };
                }
            }
        }
    } else if (dataQuery.expression != null) {
        expression = dataQuery.expression;
    } else if (dataQuery.attribute != null) {
        attribute = dataQuery.attribute;
    }

    return {
        attribute,
        expression,
        keyAttribute,
        keySelect
    };
}

export function convertAQLFilterToAttributeName(filter: IAQLFilter): IAttributeName {
    if (filter == null) {
        return null;
    }

    if (filter.syntaxTree != null) {
        return {
            expression: {
                syntaxTree: filter.syntaxTree,
                rettype: filter.rettype
            }
        };
    } else if (filter.attrib != null) {
        return {
            attribute: filter.attrib.join(ATTRIBUTE_SEPARATOR)
        };
    }

    return null;
}

export function sameAttributes(attributeName1: IAttributeName, attributeName2: IAttributeName) {
    if (attributeName1 == null || attributeName2 == null) {
        return false;
    }

    if (attributeName1.attribute != null && attributeName1.attribute === attributeName2.attribute) {
        return true;
    }

    return (
        attributeName1.expression != null &&
        attributeName2.expression != null &&
        JSON.stringify(attributeName1.expression.syntaxTree) === JSON.stringify(attributeName2.expression.syntaxTree)
    );
}

// export function sameFilters(filter1: IGlobalAttributeFilter, filter2: IGlobalAttributeFilter) {
//     if (!sameAttributes(filter1, filter2)) {
//         return false;
//     }

//     const timePart1 = filter1.intervals != null && filter1.intervals.timePart != null ? filter1.intervals.timePart : null;
//     const timePart2 = filter2.intervals != null && filter2.intervals.timePart != null ? filter2.intervals.timePart : null;

//     if (timePart1 !== timePart2) {
//         return false;
//     }

//     const options1 = filter1.options;
//     const options2 = filter2.options;
//     const relation1 = options1 != null && options1.relation != null ? options1.relation : null;
//     const relation2 = options2 != null && options2.relation != null ? options2.relation : null;

//     if (relation1 !== relation2) {
//         return false;
//     }

//     return true;
// }

export function sameAQLFilterArrays(filters1: IAQLFilter[], filters2: IAQLFilter[]) {
    if (filters1 == null || filters2 == null) {
        return false;
    }

    if (filters1.length !== filters2.length) {
        return false;
    }

    for (const fitler1 of filters1) {
        if (
            _.find(filters2, (filter2) => {
                return sameAQLFilters(fitler1, filter2);
            }) == null
        ) {
            return false;
        }
    }

    return true;
}

export function sameAQLFilters(filter1: IAQLFilter, filter2: IAQLFilter) {
    if (filter1 == null || filter2 == null) {
        return false;
    }

    if (filter1.type !== filter2.type) {
        return false;
    }

    if (filter1.type === AQL.TYPE_PERIOD) {
        return compareObjects(filter1, filter2);
    }

    if (filter1.right != null || filter2.right != null) {
        return sameAQLFilterArrays(filter1.right, filter2.right);
    }

    if (filter1.left != null || filter2.left != null) {
        return sameAQLFilterArrays(filter1.left, filter2.left);
    }

    const attribute1 = filter1.attrib != null ? filter1.attrib.join(ATTRIBUTE_SEPARATOR) : '';
    const attribute2 = filter2.attrib != null ? filter2.attrib.join(ATTRIBUTE_SEPARATOR) : '';

    if (attribute1.length > 0 && attribute1 === attribute2) {
        return true;
    }

    if (filter1.syntaxTree != null && filter2.syntaxTree != null) {
        return compareObjects(filter1.syntaxTree, filter2.syntaxTree);
    }

    return false;
}

export function getApplySwitcherState(theme: IValuesCollection) {
    const result = theme != null && theme['apply-switcher-state'];

    return result || ApplySwitcherStates.OFF_BY_DEFAULT;
}

export function useClickToFilterFunctionality(theme: IValuesCollection) {
    return theme[platformThemeVars.enableClickToFilter] === true;
}

export function applyModeIsOn(globalState: GlobalState, theme: IValuesCollection) {
    if (globalState != null) {
        return globalState.applyModeIsOn(theme);
    }

    return false;
}

export function createNewScopeOptions(parentScopeOptions: IScopeOptions, scopeID: string): IScopeOptions {
    let result: IScopeOptions;

    if (parentScopeOptions != null) {
        const parentScopeID = parentScopeOptions.scopeID;

        result = { ...parentScopeOptions };

        if (scopeID !== parentScopeID) {
            result.scopeID = scopeID;

            if (parentScopeID != null) {
                if (result.parentScopes == null) {
                    result.parentScopes = [parentScopeOptions.scopeID];
                } else if (result.parentScopes.indexOf(scopeID) < 0) {
                    result.parentScopes.push[parentScopeID];
                }
            }
        }
    } else {
        result = {
            scopeID: scopeID
        };
    }

    return result;
}

export function createNewScope(parentScopeOptions: IScopeOptions, containerID: number, tabID: number): IScopeOptions {
    return createNewScopeOptions(parentScopeOptions, containerID + '_' + tabID);
}

function convertAQLFilterToQueryFilter(filter: IAQLFilter): IQueryFilter {
    let relation: string = filter.type;

    if (relation === AQL.RELATION) {
        relation = filter.relation;
    }

    let valueType: QueryFilterValueType = 'constant';
    let value = filter.val;

    if (filter.val == null && filter.variable != null) {
        value = filter.variable;
        valueType = 'variable';
    }

    switch (relation) {
        case AQL.IN:
            relation = RELATIONS.IS_ONE_OF;
            break;

        case AQL.NOT_IN:
            relation = RELATIONS.IS_NOT_ONE_OF;
            break;

        case AQL.NOT_LIKE:
            relation = RELATIONS.NOT_LIKE;
            break;

        case AQL.IS_NULL:
            relation = RELATIONS.IS_NULL;
            value = undefined;
            break;

        case AQL.IS_NOT_NULL:
            relation = RELATIONS.IS_NOT_NULL;
            value = undefined;
            break;

        case AQL.EXISTS:
            relation = RELATIONS.EXISTS;
            value = undefined;
            break;

        case AQL.NOT_EXISTS:
            relation = RELATIONS.NOT_EXISTS;
            value = undefined;
            break;
    }

    // 'attrib' can be null in a structure created for storing in configuration. In this case 'attribute' is used.
    const attribute = filter.attrib != null ? filter.attrib.join(ATTRIBUTE_SEPARATOR) : filter['attribute'];

    let result: IQueryFilter = {
        attribute: attribute,
        relation: relation,
        valueType: valueType
    };

    if (filter.syntaxTree != null) {
        result.expression = {
            syntaxTree: filter.syntaxTree,
            rettype: filter.rettype
        };
    }

    if (value !== undefined) {
        result.value = value;
    }

    return result;
}

export function convertAQLFiltersToQueryFilters(filters: IAQLFilter[]): IQueryFilter[] {
    const result: IQueryFilter[] = [];

    if (filters != null) {
        filters.forEach((filter) => {
            let newFilter: IQueryFilter = null;

            if (filter.type === AQL.TYPE_OR) {
                const leftPart = convertAQLFiltersToQueryFilters(filter.left);
                const rightPart = convertAQLFiltersToQueryFilters(filter.right);

                newFilter = {
                    left: leftPart,
                    right: rightPart
                };
            } else {
                newFilter = convertAQLFilterToQueryFilter(filter);
            }

            if (newFilter != null) {
                result.push(newFilter);
            }
        });
    }

    return result;
}

export function deleteWigetAttributeFilters(
    filterCollection: IGlobalAttributeFilterCollection,
    staticWidgetID?: number
) {
    _.each(filterCollection, (filters) => {
        _.keys(filters).forEach((attributeID) => {
            if (filters[attributeID].widgetID === staticWidgetID) {
                delete filters[attributeID];
            }
        });
    });
}

export function deleteClickToFilters(filterCollection: IGlobalAttributeFilterCollection) {
    _.each(filterCollection, (filters) => {
        _.keys(filters).forEach((attributeID) => {
            if (filters[attributeID].clickToFilterAttribute === true) {
                delete filters[attributeID];
            }
        });
    });
}

export function dropTemporaryAttributeFilters(globalFiltercollection: IGlobalAttributeFilterCollection): void {
    _.each(globalFiltercollection, (filters) => {
        const attributesToDrop: string[] = [];

        _.each(filters, (filter, attributeID) => {
            if (filter.clickToFilterAttribute || filter.removed) {
                attributesToDrop.push(attributeID);
            }
        });

        _.each(attributesToDrop, (attributeID) => {
            delete filters[attributeID];
        });
    });
}

export function createAttributeID(
    attribute: IAttribute,
    dataSourceID: string | number,
    existingFilters?: IGlobalAttributeFilters
): string {
    let attributeName = attribute.attribute;

    if (attributeName != null) {
        if (attribute.valueType === 'variable') {
            attributeName = '@' + attributeName;
        }
    } else if (attribute.expression != null) {
        attributeName = 'Exp:' + attribute.expression.text;
    }

    let result = attributeName + '_' + dataSourceID;

    if (existingFilters != null && existingFilters[result] != null) {
        let index = 1;

        while (existingFilters[result] != null) {
            result = attributeName + '_' + index;
            ++index;
        }
    }

    return result;
}

export function mergeAttributeFilterCollections(
    existingCollection: IGlobalAttributeFilterCollection,
    newCollection: IGlobalAttributeFilterCollection,
    widgetID: number
) {
    if (widgetID != null) {
        deleteWigetAttributeFilters(existingCollection, widgetID);
    }

    _.each(newCollection, (newFilters, dataSourceID) => {
        let existingFilters = existingCollection[dataSourceID];

        if (existingFilters == null) {
            existingCollection[dataSourceID] = existingFilters = {};
        }

        _.each(newFilters, (newFilter) => {
            if (widgetID != null && newFilter.widgetID === widgetID) {
                const filterCopy = $.extend(true, {}, newFilter);
                const attributeID = createAttributeID(newFilter, dataSourceID, existingFilters);

                filterCopy.id = attributeID;
                existingFilters[attributeID] = filterCopy;
            } else if (newFilter.widgetID == null) {
                const values = Array.isArray(newFilter.values) ? [...newFilter.values] : newFilter.values;
                const existingFilter = existingFilters[newFilter.id];

                if (existingFilter != null) {
                    existingFilter.values = values;
                    delete existingFilter.removed;
                } else {
                    existingFilters[newFilter.id] = { ...newFilter };
                }
            }
        });
    });
}

function timePartIsSingleOrSequential(timePart: number | number[]) {
    let result = typeof timePart === 'number';

    if (!result && Array.isArray(timePart)) {
        const quarterLength = timePart.length;

        if (quarterLength === 1) {
            result = true;
        } else if (quarterLength > 1) {
            timePart.sort((a, b) => a - b);

            for (let index = 1; index < quarterLength; index++) {
                if (timePart[index - 1] === timePart[index] - 1) {
                    result = true;
                } else {
                    result = false;
                    break;
                }
            }
        }
    }

    return result;
}

function quarterHasAllMonths(quarter: number, months: number[]) {
    const lastQuarterMonth = quarter * 2 + quarter;

    for (let m = lastQuarterMonth - 2; m <= lastQuarterMonth; m++) {
        if (months.indexOf(m) < 0) {
            return false;
        }
    }

    return true;
}

// See Issue 99164.
export function dropNotNeededTimeParts(periodFilter: IPeriodFilter) {
    let result = periodFilter;

    if (result != null && result.from != null && result.to != null) {
        const { year, quarter, month } = result;

        result = { ...result };

        if (year == null || typeof year === 'number' || (Array.isArray(year) && year.length === 1)) {
            // No year or a single year is selected.
            if (quarter != null && timePartIsSingleOrSequential(quarter)) {
                delete result.quarter;
            }

            if (month != null && timePartIsSingleOrSequential(month)) {
                delete result.month;
            }

            if (result.quarter == null && result.month == null) {
                delete result.year;
            }
        } else if (Array.isArray(year) && year.length > 1 && quarter == null && month == null) {
            // Many years are selected only.
            delete result.year;
        }

        if (quarter != null && Array.isArray(month) && month.length > 2) {
            if (typeof quarter === 'number') {
                if (quarterHasAllMonths(quarter, month)) {
                    delete result.quarter;
                }
            } else if (Array.isArray(quarter)) {
                if (quarter.length > 0) {
                    const newQuarter: number[] = [];

                    quarter.forEach((q) => {
                        if (!quarterHasAllMonths(q, month)) {
                            newQuarter.push(q);
                        }
                    });

                    if (newQuarter.length > 0) {
                        if (newQuarter.length !== quarter.length) {
                            result.quarter = newQuarter;
                        }
                    } else {
                        delete result.quarter;
                    }
                } else {
                    delete result.quarter;
                }
            }
        }
    }

    return result;
}

export function checkGlobalFilterControlAttributes(
    attributeFilter: IGlobalFilterControl_AttributeFilter,
    dataSourceID: number,
    runTimeWidgetID: number,
    glob: IGlob
) {
    const { attributesToAllow, attributesToIgnore, attributesToOverride } = attributeFilter;
    const notFoundAttributes: string[] = [];

    if (Array.isArray(attributesToAllow) && attributesToAllow.length > 0) {
        attributesToAllow.forEach((attribute) => {
            if (
                attribute.attribute != null &&
                glob.EntityModelsStore.getEntity(attribute.attribute, dataSourceID) == null
            ) {
                notFoundAttributes.push(attribute.attribute);
            }
        });
    }

    if (Array.isArray(attributesToIgnore) && attributesToIgnore.length > 0) {
        attributesToIgnore.forEach((attribute) => {
            if (
                attribute.attribute != null &&
                glob.EntityModelsStore.getEntity(attribute.attribute, dataSourceID) == null
            ) {
                notFoundAttributes.push(attribute.attribute);
            }
        });
    }

    if (Array.isArray(attributesToOverride) && attributesToOverride.length > 0) {
        attributesToOverride.forEach(({ oldAttribute, newAttribute }) => {
            if (oldAttribute != null && glob.EntityModelsStore.getEntity(oldAttribute, dataSourceID) == null) {
                notFoundAttributes.push(oldAttribute);
            }

            if (newAttribute != null && glob.EntityModelsStore.getEntity(newAttribute, dataSourceID) == null) {
                notFoundAttributes.push(newAttribute);
            }
        });
    }

    if (notFoundAttributes.length > 0) {
        glob.AppActions.showNotification_Fail(
            `Some Attributes specified in Global Filter Control do not exist: '${notFoundAttributes.join(`', '`)}'`,
            runTimeWidgetID
        );
    }

    return attributeFilter;
}
